import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IGetAllCombosForSearchUsersResponse,
  IGetUsersByCompanyIdWithRecommendsResponse,
  IUserReport,
  TCompanyLabeledOption,
  TQuestionReportCommentResponse,
  TRecommendationLabeledOption,
} from "../types/users.types";
import { userApi } from "../api/user.api";
import {
  INumberOption,
  TSpecialization,
  TTherapistSpecialization,
} from "../../types/types";
import i18n from "../../i18n";
import { IPendingReportResponce, TPendingInquiry, TPendingReport } from "../types/user.types";

type TUsersState = {
  users: IUserReport[];
  companies: TCompanyLabeledOption[];
  recommendations: TRecommendationLabeledOption[];
  roles: INumberOption[];
  questionReportComments: TQuestionReportCommentResponse[];
  specializations: TSpecialization[];
  scroll: {
    currentUser: number | null;
    currentQuestionnaire: number | null;
    currentRRM: number | null;
    currentRRMQuestionnaire: number | null;
    currentProtocol: number | null;
    currentSMS: number | null;
    currentComponent: number | null;
    currentProtocolComponent: number | null;
  };
  pendingReportAppointments: TPendingReport[];
  pendingReportInquiries: TPendingInquiry[];
  totalPendingReports: number;
};
const initialState: TUsersState = {
  users: [],
  companies: [],
  recommendations: [],
  roles: [],
  questionReportComments: [],
  specializations: [],
  scroll: {
    currentUser: null,
    currentQuestionnaire: null,
    currentRRM: null,
    currentRRMQuestionnaire: null,
    currentProtocol: null,
    currentSMS: null,
    currentComponent: null,
    currentProtocolComponent: null,
  },
  pendingReportAppointments: [],
  pendingReportInquiries: [],
  totalPendingReports: 0,
};

export const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearQuestionReportComments: (state) => {
      return { ...state, questionReportComments: [] };
    },
    setCurrentQuestionnaire: (state, { payload }) => {
      return {
        ...state,
        scroll: {
          ...state.scroll,
          currentQuestionnaire: payload,
          currentRRM: state.scroll.currentRRM,
        },
      };
    },
    setCurrentRRM: (state, { payload }) => {
      return {
        ...state,
        scroll: { ...state.scroll, currentRRM: payload },
      };
    },
    setCurrentRRMQuestionnaire: (state, { payload }) => {
      return {
        ...state,
        scroll: { ...state.scroll, currentRRMQuestionnaire: payload },
      };
    },
    setCurrentProtocol: (state, { payload }) => {
      return {
        ...state,
        scroll: { ...state.scroll, currentProtocol: payload },
      };
    },
    setCurrentSMS: (state, { payload }) => {
      return {
        ...state,
        scroll: { ...state.scroll, currentSMS: payload },
      };
    },
    setCurrentComponent: (state, { payload }) => {
      return {
        ...state,
        scroll: { ...state.scroll, currentComponent: payload },
      };
    },
    setCurrentProtocolComponent: (state, { payload }) => {
      return {
        ...state,
        scroll: { ...state.scroll, currentProtocolComponent: payload },
      };
    },
    setCurrentUser: (state, { payload }) => {
      return {
        ...state,
        scroll: { ...state.scroll, currentUser: payload },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        userApi.endpoints.getUsersByCompanyIdWithRecommends.matchFulfilled,
        (state: TUsersState, action) => {
          const payload =
            action.payload as IGetUsersByCompanyIdWithRecommendsResponse;
          const args = action.meta?.arg?.originalArgs as any;

          // אל תעדכן את users אם זו קריאה למטפלים בלבד (לצורך dropdown)
          // קריאה למטפלים = roleIds: [-1] בלבד, ללא פילטרים אחרים
          const isTherapistsOnlyQuery =
            args?.roleIds?.length === 1 &&
            args?.roleIds[0] === -1 &&
            args?.hasTreatmentProgram === undefined &&
            (!args?.fundingSourceIds ||
              (args.fundingSourceIds.length === 1 &&
                args.fundingSourceIds[0] === 0)) &&
            (!args?.treatmentTypeIds ||
              (args.treatmentTypeIds.length === 1 &&
                args.treatmentTypeIds[0] === 0)) &&
            (!args?.therapistGuids || args.therapistGuids.length === 0) &&
            (!args?.specializationIds ||
              (args.specializationIds.length === 1 &&
                args.specializationIds[0] === 0));

          if (isTherapistsOnlyQuery || args?.isInternal) {
            // אל תעדכן את users, זו קריאה פנימית (dropdown או טבלה משנית)
            return state;
          }

          return {
            ...state,
            users: payload.Users,
          };
        }
      )
      .addMatcher(
        userApi.endpoints.getAllCombosForSearchUsers.matchFulfilled,
        (
          state: TUsersState,
          { payload }: { payload: IGetAllCombosForSearchUsersResponse }
        ) => {
          return {
            ...state,
            companies: payload.CompaniesList.map((c) => {
              const currentLang = i18n.language || "he";
              let label = c.Name; // Default to Name

              if (currentLang !== "he") {
                label = c.NameEn || c.Name;
              }

              return {
                ...c,
                label,
                value: c.Id,
              };
            }),
            recommendations: payload.RecommendsList.map((r) => ({
              ...r,
              label: r.Desc,
              value: r.Id,
            })),
            roles: payload.RolesList.map((r) => ({
              label: r.Name,
              value: r.Id,
            })),
          };
        }
      )
      .addMatcher(
        userApi.endpoints.getCommentsToRequest.matchFulfilled,
        (
          state: TUsersState,
          action: PayloadAction<TQuestionReportCommentResponse[]>
        ) => {
          return {
            ...state,
            questionReportComments: action.payload,
          };
        }
      )
      .addMatcher(
        userApi.endpoints.therapistsSpecializationsDML.matchFulfilled,
        (state: TUsersState, action: PayloadAction<TSpecialization[]>) => {
          return {
            ...state,
            specializations: action.payload,
          };
        }
      )
      .addMatcher(
        userApi.endpoints.therapistsSpecializations2TherapistsDML
          .matchFulfilled,
        (
          state: TUsersState,
          { payload }: { payload: TTherapistSpecialization[] }
        ) => {
          state.users = state.users.map((user) => {
            if (user.Users.Id === payload[0]?.usersId) {
              return {
                ...user,
                Users: {
                  ...user.Users,
                  TherapistSpecializations: payload,
                },
              };
            }
            return user;
          });
        }
      )
      .addMatcher(
        userApi.endpoints.addUser.matchFulfilled,
        (state: TUsersState, { payload }: { payload: any }) => {
          // עדכון משתמש קיים ברשימה או הוספת משתמש חדש
          if (payload && payload.Users) {
            const updatedUser = payload.Users[0];
            const userIndex = state.users.findIndex(
              (u) => u.Users.Id === updatedUser.Id
            );

            if (userIndex !== -1) {
              // עדכון משתמש קיים
              state.users[userIndex] = {
                ...state.users[userIndex],
                Users: updatedUser,
              };
            } else {
              // הוספת משתמש חדש
              state.users.push({
                AvailabilityHours: null,
                AppointmentsProgramsNum: null,
                AppointmentsProgramStatus: undefined,
                TotalReports: 0,
                Recommends: null,
                RoleName: null,
                Users: updatedUser,
              });
            }
          }
        }
      )
      // Status toggle: patch the row in place. The response carries the status the server
      // actually stored, so the table reflects it without refetching (and without depending
      // on the filters/pagination the page happens to have active).
      .addMatcher(
        userApi.endpoints.updateUserStatus.matchFulfilled,
        (
          state: TUsersState,
          { payload }: { payload: { guid: string; statusId: number } }
        ) => {
          if (!payload?.guid) return;
          const guid = String(payload.guid).toLowerCase();
          state.users = state.users.map((u) =>
            String(u.Users.Guid ?? "").toLowerCase() === guid
              ? { ...u, Users: { ...u.Users, StatusId: payload.statusId } }
              : u
          );
        }
      )
      .addMatcher(
        userApi.endpoints.getPendingReports.matchFulfilled,
        (state: TUsersState, { payload }: { payload: IPendingReportResponce }) => {
          state.pendingReportAppointments = payload.pendingAppointments;
          state.pendingReportInquiries = payload.notReported;
          state.totalPendingReports = payload.notReportedTotal + payload.pendingAppointmentsTotal;
        }
      );
  },
});

export const { actions, reducer } = usersSlice;
export default usersSlice.reducer;
